/* global Image */

/**
 * Share Image Generator
 * Generates a shareable image with player results using HTML5 Canvas
 *
 * @param {Object} playerData - Player information
 * @param {string} playerData.name - Player name
 * @param {string} playerData.avatar - Avatar filename
 * @param {number} playerData.score - Final score
 * @param {number} playerData.turns - Turns taken
 * @param {Object} gameData - Game information
 * @param {string} gameData.boardSize - Board size (xs, s, m, l, xl)
 * @param {number} gameData.cardCount - Total cards in game
 * @param {Object} translations - Translation object
 * @returns {Promise<Blob>} - Image blob ready for sharing
 */
export async function generateResultImage (playerData, gameData, translations, theme = 'light') {
  // Canvas dimensions optimized for mobile sharing (1080x1080 Instagram format)
  const CANVAS_WIDTH = 1080
  const CANVAS_HEIGHT = 1080

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT
  const ctx = canvas.getContext('2d')

  // Colors (dynamic based on theme)
  const COLORS = {
    background: theme === 'light' ? '#43a1d5' : '#173e69',
    backgroundEnd: theme === 'light' ? '#173e69' : '#2a2a2a',
    cardBackground: '#ffffff',
    textPrimary: '#173e69', // Dark blue
    textSecondary: '#666666',
    accent: '#f0c430' // Gold accent
  }

  try {
    // 1. Draw background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT)
    gradient.addColorStop(0, COLORS.background)
    gradient.addColorStop(1, COLORS.backgroundEnd)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // 2. Draw main card container
    const cardPadding = 80
    const cardX = cardPadding
    const cardY = cardPadding
    const cardWidth = CANVAS_WIDTH - (cardPadding * 2)
    const cardHeight = CANVAS_HEIGHT - (cardPadding * 2)

    ctx.fillStyle = COLORS.cardBackground
    ctx.shadowColor = 'rgb(0 0 0 / 0.2)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetY = 10
    roundRect(ctx, cardX, cardY, cardWidth, cardHeight, 20)
    ctx.fill()
    ctx.shadowColor = 'transparent'

    // 3. Draw Memodiez logo
    await drawLogo(ctx, CANVAS_WIDTH / 2, cardY + 100, 200, theme)

    // 4. Load and draw player avatar
    const avatarSize = 200
    const avatarX = (CANVAS_WIDTH - avatarSize) / 2
    const avatarY = cardY + 150

    await drawPlayerAvatar(ctx, playerData.avatar, avatarX, avatarY, avatarSize)

    // 5. Draw player name
    ctx.fillStyle = COLORS.textPrimary
    ctx.font = 'bold 48px Arial, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(playerData.name, CANVAS_WIDTH / 2, avatarY + avatarSize + 60)

    // 6. Draw game results
    const resultsY = avatarY + avatarSize + 140
    drawGameResults(ctx, playerData, gameData, translations, resultsY, COLORS)

    // 7. Draw call to action
    ctx.fillStyle = COLORS.textPrimary
    ctx.font = 'bold 36px Arial, sans-serif'
    ctx.textAlign = 'center'
    const callToAction = translations.memorama.share.callToAction || '¿Podés superarme? Jugá gratis en'
    ctx.fillText(callToAction, CANVAS_WIDTH / 2, CANVAS_HEIGHT - 180)

    // 8. Draw footer with website
    ctx.fillStyle = COLORS.textSecondary
    ctx.font = '32px Arial, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('memodiez.com', CANVAS_WIDTH / 2, CANVAS_HEIGHT - 120)

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/png', 0.9)
    })
  } catch (error) {
    console.error('Error generating share image:', error)
    throw new Error('Failed to generate share image')
  }
}

/**
 * Draw rounded rectangle helper
 */
function roundRect (ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 *
 * Draw memodiez logo
 * */

async function drawLogo (ctx, centerX, centerY, maxWidth, theme) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // For CORS if needed

    img.onload = () => {
      // Calculate size maintaining aspect ratio
      const aspectRatio = img.width / img.height
      const logoWidth = Math.min(maxWidth, img.width)
      const logoHeight = logoWidth / aspectRatio

      const x = centerX - logoWidth / 2
      const y = centerY - logoHeight / 2

      // Apply inversion filter for dark theme if needed
      if (theme === 'dark') {
        ctx.filter = 'invert(1)'
      }

      ctx.drawImage(img, x, y, logoWidth, logoHeight)

      // Reset filter
      ctx.filter = 'none'

      resolve()
    }

    img.onerror = () => {
      // Fallback: draw a placeholder
      ctx.fillStyle = '#173e69'
      ctx.font = 'bold 64px Arial, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('MEMODIEZ', centerX, centerY)
      resolve()
    }

    img.src = '/assets/logos/logo.webp'
  })
}

/**
 * Load and draw player avatar
 */
async function drawPlayerAvatar (ctx, avatarFilename, x, y, size) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // For CORS if needed

    img.onload = () => {
      // Save context for clipping
      ctx.save()

      // Create circular clipping path
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, 2 * Math.PI)
      ctx.clip()

      // Draw avatar image
      ctx.drawImage(img, x, y, size, size)

      // Restore context
      ctx.restore()

      // Draw border around avatar
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, 2 * Math.PI)
      ctx.strokeStyle = '#173e69'
      ctx.lineWidth = 4
      ctx.stroke()

      resolve()
    }

    img.onerror = () => {
      // Fallback: draw default avatar placeholder
      ctx.fillStyle = '#e0e0e0'
      ctx.beginPath()
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, 2 * Math.PI)
      ctx.fill()

      // Draw default avatar icon (simple person silhouette)
      ctx.fillStyle = '#666666'
      ctx.font = `${size * 0.4}px Arial, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('👤', x + size / 2, y + size / 2)

      resolve()
    }

    // Load avatar image
    const avatarPath = avatarFilename ? `/avatars/${avatarFilename}` : '/avatars/avatar-default.webp'
    img.src = avatarPath
  })
}

/**
 * Draw game results section
 */
function drawGameResults (ctx, playerData, gameData, translations, startY, colors) {
  const centerX = ctx.canvas.width / 2
  const lineHeight = 70
  let currentY = startY

  // Results title
  ctx.fillStyle = colors.textPrimary
  ctx.font = 'bold 42px Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(translations.memorama.share.resultsTitle || 'Resultados', centerX, currentY)
  currentY += lineHeight + 20

  // Game info line
  ctx.fillStyle = colors.textSecondary
  ctx.font = '36px Arial, sans-serif'
  const gameInfo = `${gameData.cardCount} ${translations.memorama.share.cards || 'tarjetas'} • ${playerData.turns} ${translations.memorama.share.turns || 'turnos'}`
  ctx.fillText(gameInfo, centerX, currentY)
  currentY += lineHeight

  // Score (highlighted)
  ctx.fillStyle = colors.accent
  ctx.font = 'bold 56px Arial, sans-serif'
  ctx.fillText(`${playerData.score} ${translations.memorama.share.points || 'puntos'}`, centerX, currentY)
}

/**
 * Generate share text for social media
 */
export function generateShareText (playerData, translations) {
  const shareText = (translations.memorama.share.text ||
    '¡Completé el juego Memodiez en {turns} turnos con {points} puntos!')
    .replace('{turns}', playerData.turns)
    .replace('{points}', playerData.score)

  const callToAction = translations.memorama.share.callToAction ||
    '¿Podés superarme? Jugá gratis en'

  const website = 'memodiez.com'

  return `${shareText}\n\n${callToAction}\n${website}`
}
